import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import UserInfo from "../../../../components/SVGComponents/UserInfo";

/**
 * Patient information overlay for an Encapsulated PDF viewport.
 *
 * A DOC viewport renders the document itself, so none of the usual overlays
 * (PatientDetails / StudyDetails / SeriesDetails) apply — the PDF fills the pane
 * and the DICOM tags wrapped around it would otherwise be invisible. This is the
 * same affordance a viewport gets in a dense layout: the UserInfo icon from
 * MobileOverlay, which opens a popup with the details on click. The popup reuses
 * that component's overlay classes so the text matches every other overlay in the
 * viewer; the icon is laid out in the viewport's own toolbar row instead of being
 * positioned over the document, which the embedded PDF viewer's toolbar occupies.
 *
 * Values come from the instance's own header (see readDocumentMetadata), not
 * from the series list, so what is shown is what the document actually carries.
 */
const EncapsulatedDocOverlay = ({ metadata }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (!metadata) return null;

  const genderOf = (sex) => {
    if (sex === "M") return t("viewer.overlay.male");
    if (sex === "F") return t("viewer.overlay.female");
    // Anything else (O, blank, a site-specific code) is shown as sent rather
    // than forced into one of the two labels.
    return sex;
  };

  // Only rows the instance actually carries — an Encapsulated PDF from a report
  // gateway often fills in far less than a scanner does, and blank rows would
  // push the ones that matter out of the popup.
  const rows = [
    ["viewer.overlay.document-title", metadata.title],
    ["viewer.overlay.patient-name", metadata.patientName],
    ["viewer.overlay.patient-id", metadata.patientId],
    ["viewer.overlay.birth-date", metadata.patientBirthDate],
    ["viewer.overlay.gender", genderOf(metadata.patientSex)],
    ["viewer.overlay.age", metadata.patientAge?.replace(/^0+/, "")],
    ["viewer.overlay.study-date", metadata.studyDate],
    ["viewer.overlay.study-id", metadata.studyId],
    ["viewer.overlay.study-description", metadata.studyDescription],
    ["viewer.overlay.accession-number", metadata.accessionNumber],
    ["viewer.overlay.referring-physician", metadata.referringPhysicianName],
    ["viewer.overlay.institution", metadata.institutionName],
    ["viewer.overlay.modality", metadata.modality],
    ["viewer.overlay.series", metadata.seriesNumber],
    ["viewer.overlay.image", metadata.instanceNumber],
    ["viewer.overlay.content-date", metadata.contentDateTime],
  ].filter(([, value]) => value);

  return (
    <>
      <div
        className="viewport-pdf-info-toggle"
        title={t("viewer.overlay.patient-information")}
        onClick={(e) => {
          // The pane's click handler activates the viewport; toggling the popup
          // should not also be read as a click on the document behind it.
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        <UserInfo color={isOpen ? "#2F80ED" : "rgba(255, 255, 255, 0.42)"} />
      </div>
      {isOpen && (
        <div
          className="viewport-overlay-mobile-popup react-scroll"
          onClick={(e) => e.stopPropagation()}
        >
          {rows.map(([labelKey, value], index) => (
            <div
              key={labelKey}
              style={{ lineHeight: "12px", marginTop: index === 0 ? 0 : "5px" }}
            >
              <span className="viewport-overlay-mobile-key">
                {t(labelKey)}:{" "}
              </span>
              <span className="viewport-overlay-mobile-value">{value}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default EncapsulatedDocOverlay;
